import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - worker as URL
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

export interface ParsedFile {
  fileName: string;
  text: string;
  wordCount: number;
  sections: { name: string; content: string }[];
}

const SECTION_KEYWORDS = [
  'abstract', 'introduction', 'background', 'literature review',
  'methodology', 'methods', 'approach', 'materials',
  'results', 'findings', 'analysis', 'evaluation',
  'discussion', 'implications',
  'conclusion', 'conclusions', 'future work',
  'references', 'bibliography',
];

export async function parseFile(file: File): Promise<ParsedFile> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.docx')) return parseDocx(file);
  if (lower.endsWith('.pdf')) return parsePdf(file);
  if (lower.endsWith('.txt') || lower.endsWith('.md')) return parsePlain(file);
  throw new Error('Unsupported file type. Please upload .docx, .pdf, .txt or .md');
}

async function parseDocx(file: File): Promise<ParsedFile> {
  const arrayBuffer = await file.arrayBuffer();
  const { value } = await mammoth.extractRawText({ arrayBuffer });
  return finalize(file.name, value);
}

async function parsePdf(file: File): Promise<ParsedFile> {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const pages: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    pages.push(content.items.map((it: any) => it.str).join(' '));
  }
  return finalize(file.name, pages.join('\n\n'));
}

async function parsePlain(file: File): Promise<ParsedFile> {
  return finalize(file.name, await file.text());
}

function finalize(fileName: string, text: string): ParsedFile {
  const clean = text.replace(/\r\n/g, '\n').replace(/\t/g, ' ').trim();
  const words = clean.split(/\s+/).filter(Boolean).length;
  return { fileName, text: clean, wordCount: words, sections: splitSections(clean) };
}

/** Heuristic section splitter. Looks for lines that match known headings (case-insensitive). */
function splitSections(text: string) {
  const lines = text.split('\n');
  const pattern = new RegExp(`^\\s*(?:\\d+[.\\s]+)?(${SECTION_KEYWORDS.join('|')})\\s*:?\\s*$`, 'i');
  const out: { name: string; content: string }[] = [];
  let current = { name: 'Preamble', content: '' };
  for (const line of lines) {
    const m = line.trim().match(pattern);
    if (m) {
      if (current.content.trim()) out.push({ ...current, content: current.content.trim() });
      current = { name: titleCase(m[1]), content: '' };
    } else {
      current.content += line + '\n';
    }
  }
  if (current.content.trim()) out.push({ ...current, content: current.content.trim() });
  return out.length > 1 ? out : [{ name: 'Full document', content: text }];
}

function titleCase(s: string) {
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}
